import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { FaEye, FaSpinner, FaCheckCircle, FaExclamationCircle } from 'react-icons/fa';
import { toast } from 'react-toastify';

const ViewChecker = ({ platform, videoLink, adId, proofIndex, onViewsFound }) => {
    const [loading, setLoading] = useState(false);
    const [views, setViews] = useState(null);
    const [timer, setTimer] = useState(0);
    const [error, setError] = useState(null);

    useEffect(() => {
        let interval;
        if (loading && timer > 0) {
            interval = setInterval(() => {
                setTimer((prev) => prev - 1);
            }, 1000);
        } else if (timer === 0) {
            clearInterval(interval);
        }
        return () => clearInterval(interval);
    }, [loading, timer]);

    if (platform?.toLowerCase() === 'instagram') {
        return (
            <div className="flex items-center gap-2 px-3 py-1.5 bg-green-50 text-green-700 rounded-lg text-sm font-medium border border-green-200">
                <FaCheckCircle className="w-4 h-4" />
                <span>Admin Verification Required</span>
            </div>
        );
    }

    const handleCheckViews = async () => {
        if (!platform || !videoLink) {
            toast.error("Platform or video link is missing");
            return;
        }

        setLoading(true);
        setTimer(35); // Start 35 second timer
        setError(null);
        setViews(null);

        try {
            const response = await axios.post(
                `${process.env.REACT_APP_API_BASE_URL}/check/current/view`,
                {
                    platform: platform,
                    videoUrl: videoLink
                },
                {
                    headers: {
                        Authorization: `Bearer ${localStorage.getItem("token")}`,
                        'Content-Type': 'application/json',
                    },
                }
            );

            if (response.data.success) {
                setViews(response.data.views);
                if (onViewsFound) {
                    onViewsFound(response.data.views);
                }
                toast.success(`Views found: ${response.data.views}`);
            } else {
                setError("Failed to fetch views");
                toast.error("Failed to fetch current views");
            }
        } catch (err) {
            console.error('Error checking current view:', err);
            setError("Error checking views");
            toast.error("Failed to check current views. Please try again.");
        } finally {
            setLoading(false);
            setTimer(0);
        }
    };

    return (
        <div className="flex flex-col items-start gap-2">
            {!views && !loading && (
                <button
                    onClick={handleCheckViews}
                    className="flex items-center gap-2 px-3 py-1.5 bg-blue-50 text-blue-600 hover:bg-blue-100 rounded-lg text-sm font-medium transition-colors border border-blue-200"
                    title="Check current views"
                >
                    <FaEye className="w-4 h-4" />
                    Check Views
                </button>
            )}

            {loading && (
                <div className="flex items-center gap-2 px-3 py-1.5 bg-yellow-50 text-yellow-700 rounded-lg text-sm font-medium border border-yellow-200">
                    <FaSpinner className="w-4 h-4 animate-spin" />
                    <span>Checking... {timer}s</span>
                </div>
            )}

            {views && (
                <div className="flex items-center gap-2 px-3 py-1.5 bg-green-50 text-green-700 rounded-lg text-sm font-medium border border-green-200">
                    <FaCheckCircle className="w-4 h-4" />
                    <span>{views}</span>
                    <button
                        onClick={handleCheckViews}
                        className="ml-2 text-xs text-green-600 underline hover:text-green-800"
                        title="Check again"
                    >
                        Refresh
                    </button>
                </div>
            )}

            {error && !loading && (
                <div className="flex items-center gap-2 px-3 py-1.5 bg-red-50 text-red-700 rounded-lg text-sm font-medium border border-red-200">
                    <FaExclamationCircle className="w-4 h-4" />
                    <span>Error</span>
                    <button
                        onClick={handleCheckViews}
                        className="ml-2 text-xs text-red-600 underline hover:text-red-800"
                    >
                        Retry
                    </button>
                </div>
            )}
        </div>
    );
};

export default ViewChecker;
